import React, { useState, useEffect, ReactElement, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  IconButton,
  Button,
  Tooltip,
  Dialog,
  TextField,
  Select,
  MenuItem,
  Chip,
  Divider,
  Stack,
  Modal
} from '@mui/material';
import { TreeView, TreeItem } from '@mui/lab';
import {
  Edit as EditIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useChartOfAccountsService } from '../../hooks/useChartOfAccountsService';
import { Account, AccountType } from '../../types/accounting';
import { AccountForm } from './forms/AccountForm';
import { AccountDetailsDrawer as AccountDetails } from './drawers/AccountDetailsDrawer';
import AdvancedFilters, { FilterField } from '../common/AdvancedFilters';
import './ChartOfAccounts.css';
import { CHART_OF_ACCOUNTS } from'../../../utils/accountingCodes';

// Enhanced styled components
const GradientBorderPaper = styled(Paper)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: 'rgb(17, 25, 35)',
  color: 'rgba(255, 255, 255, 0.9)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '4px',
    background: `linear-gradient(90deg, #0EA5E9 0%, #10B981 100%)`
  }
}));

const StyledTreeItem = styled(TreeItem)(({ theme }) => ({
  '& .MuiTreeItem-content': {
    color: 'rgba(255, 255, 255, 0.9)',
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)'
    },
    '&.Mui-selected': {
      backgroundColor: 'rgba(14, 165, 233, 0.15)'
    }
  },
  '& .MuiTreeItem-group': {
    marginLeft: theme.spacing(2),
    borderLeft: `1px dashed rgba(255, 255, 255, 0.12)`
  }
}));

const CategoryChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(14, 165, 233, 0.15)',
  color: 'rgba(255, 255, 255, 0.9)',
  margin: theme.spacing(0.5),
}));

// Update the FilterType to match AdvancedFilters expectations
interface FilterType extends Record<string, any> {
  type: string;
  category: string;
  search: string;
  showInactive: boolean;
  taxCategory: string;
  fundType: string;
}

// Define the filter fields
const filterFields: FilterField[] = [
  {
    name: 'type',
    type: 'select',
    label: 'Type',
    options: [
      { value: '', label: 'All Types' },
      { value: 'ASSET', label: 'Assets' },
      { value: 'LIABILITY', label: 'Liabilities' },
      { value: 'EQUITY', label: 'Equity' },
      { value: 'REVENUE', label: 'Revenue' },
      { value: 'EXPENSE', label: 'Expenses' }
    ]
  },
  {
    name: 'category',
    type: 'select',
    label: 'Category',
    options: [
      { value: '', label: 'All Categories' },
      { value: 'OPERATING', label: 'Operating' },
      { value: 'RESTRICTED', label: 'Restricted' },
      { value: 'PROGRAM', label: 'Program' }
    ]
  }
];

// Use the imported component directly as AccountFilters
const AccountFilters = AdvancedFilters;

export const ChartOfAccounts: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [filters, setFilters] = useState<FilterType>({
    type: '',
    category: '',
    search: '',
    showInactive: false,
    taxCategory: '',
    fundType: ''
  });

  const chartOfAccountsService = useChartOfAccountsService();

  const loadAccounts = useCallback(async () => {
    try {
      const accountData = await (chartOfAccountsService as any).getAccounts(filters);
      setAccounts(accountData);
    } catch (error) {
      console.error('Error loading accounts:', error);
    }
  }, [filters, chartOfAccountsService]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const handleAccountCreate = async (accountData: Partial<Account>): Promise<void> => {
    try {
      await (chartOfAccountsService as any).createAccount({
        ...accountData,
        taxCategory: accountData.taxCategory || 'GENERAL',
        isRestricted: accountData.isRestricted || false,
        fundType: accountData.fundType,
        allowsDirectDonations: accountData.allowsDirectDonations || false
      });
      loadAccounts();
      setIsFormOpen(false);
    } catch (error) {
      console.error('Error creating account:', error);
    }
  };

  const handleAccountUpdate = async (
    accountId: string, 
    accountData: Partial<Account>
  ): Promise<void> => {
    try {
      await (chartOfAccountsService as any).updateAccount(accountId, accountData);
      loadAccounts();
      setSelectedAccount(null);
    } catch (error) {
      console.error('Error updating account:', error);
    }
  };

  const handleFilterChange = (newFilters: Record<string, any>) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      ...newFilters
    }));
  };

  const handleFormSubmit = async (data: Partial<Account>): Promise<void> => {
    try {
      if (selectedAccount) {
        await handleAccountUpdate(selectedAccount.id, data);
      } else {
        await handleAccountCreate(data);
      }
      setIsFormOpen(false);
      setSelectedAccount(null);
    } catch (error) {
      console.error('Error saving account:', error);
    }
  };

  const renderAccountTree = (accounts: Account[]) => {
    const buildTree = (accounts: Account[], parentId: string | null = null): ReactElement[] => {
      return accounts
        .filter(account => account.parent_id === parentId)
        .map(account => (
          <StyledTreeItem
            key={account.id}
            nodeId={account.id}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">
                    {account.code} - {account.name}
                  </Typography>
                  <CategoryChip 
                    label={account.category} 
                    size="small"
                  />
                  {account.isRestricted && (
                    <Chip
                      size="small"
                      label="Restricted"
                      color="warning"
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {account.balance?.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                  </Typography>
                  <Tooltip title="Edit Account">
                    <IconButton size="small" onClick={() => setSelectedAccount(account)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            }
          >
            {buildTree(accounts, account.id)}
          </StyledTreeItem>
        ));
    };

    return buildTree(accounts);
  };

  const renderAccountCategory = (category: string, accounts: any[]) => {
    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
          {category}
        </Typography>
        {accounts.map((account) => (
          <Box key={account.code} sx={{ ml: 2, mb: 2 }}>
            <Typography sx={{ color: '#fff' }}>
              {account.code} - {account.name}
            </Typography>
            {account.subcategories?.map((sub: any) => (
              <Box key={sub.code} sx={{ ml: 4 }}>
                <Typography sx={{ color: '#8b94b4' }}>
                  {sub.code} - {sub.name}
                </Typography>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ height: '100%', p: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <GradientBorderPaper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h5">Chart of Accounts</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setIsFormOpen(true)}
                sx={{
                  background: 'linear-gradient(90deg, #0EA5E9 0%, #10B981 100%)',
                  color: 'white'
                }}
              >
                Add Account
              </Button>
            </Box>
            
            <AccountFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              fields={filterFields}
              showTaxCategories={true}
              showFundTypes={true}
            />

            <TreeView
              defaultCollapseIcon={<ExpandMoreIcon />}
              defaultExpandIcon={<ChevronRightIcon />}
              sx={{ mt: 3 }}
            >
              {renderAccountTree(accounts)}
            </TreeView>
          </GradientBorderPaper>
        </Grid>
      </Grid>

      <Modal
        open={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        aria-labelledby="create-account-modal"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: 800,
          outline: 'none'
        }}>
          <AccountForm 
            onSubmit={handleFormSubmit}
            onCancel={() => setIsFormOpen(false)}
            account={selectedAccount}
            accounts={[]}
            chartOfAccounts={{}}
          />
        </Box>
      </Modal>

      <AccountDetails
        account={selectedAccount}
        open={!!selectedAccount}
        onClose={() => setSelectedAccount(null)}
        onUpdate={handleAccountUpdate}
        showTaxCategories={true}
        showFundTypes={true}
        showRestrictions={true}
      />
    </Box>
  );
}; 